import type { Theme } from '@mui/material';

/**
 * 부드러운 색상 및 transform 변화를 위한 transition
 * 버튼, 카드 등 인터랙티브 요소의 hover/focus 상태 변화에 사용
 * 
 * @param theme - MUI Theme 객체
 * @returns CSS transition 문자열
 * 
 * @example
 * ```tsx
 * const theme = useTheme();
 * const transition = smoothColorTransform(theme);
 * 
 * <Box sx={{ transition }}>
 *   Content
 * </Box>
 * ```
 */
export const smoothColorTransform = (theme: Theme): string => {
  return theme.transitions.create(
    ['background-color', 'border-color', 'transform', 'box-shadow', 'outline', 'color'],
    {
      duration: theme.transitions.duration.shorter, // 200ms
      easing: theme.transitions.easing.easeInOut,
    }
  );
};

/**
 * 빠른 상태 변화를 위한 transition
 * 토글, 스위치 등 즉각적인 피드백이 필요한 요소에 사용
 * 
 * @param theme - MUI Theme 객체
 * @returns CSS transition 문자열
 */
export const quickStateChange = (theme: Theme): string => {
  return theme.transitions.create(
    ['background-color', 'color', 'opacity'],
    {
      duration: theme.transitions.duration.shortest, // 150ms
      easing: theme.transitions.easing.sharp,
    }
  );
};

/**
 * 표준적인 상호작용을 위한 transition
 * 일반적인 UI 요소의 상태 변화에 사용
 * 
 * @param theme - MUI Theme 객체
 * @returns CSS transition 문자열
 */
export const standardInteraction = (theme: Theme): string => {
  return theme.transitions.create(
    ['background-color', 'color', 'border-color'],
    {
      duration: theme.transitions.duration.standard, // 300ms
      easing: theme.transitions.easing.easeInOut,
    }
  );
};

/**
 * 호버 시 살짝 올라가는 효과를 위한 transition
 * 카드, 버튼 등 호버 시 transform 효과가 필요한 요소에 사용
 * 
 * @param theme - MUI Theme 객체
 * @param options - 옵션 객체
 * @param options.defaultLift - 기본 lift 값 (기본값: '-1px', Button과 동일)
 * @returns CSS transition 문자열
 * 
 * @example
 * ```tsx
 * const theme = useTheme();
 * const transition = hoverLift(theme); // 기본값: -1px
 * const transitionLarge = hoverLift(theme, { defaultLift: '-8px' }); // 커스텀 값
 * 
 * <Box
 *   sx={{
 *     transition,
 *     '&:hover': {
 *       transform: `translateY(${defaultLift})`, // transition 함수에서 반환된 값 사용
 *     },
 *   }}
 * >
 * ```
 */
export const hoverLift = (theme: Theme, options?: { defaultLift?: string }): string => {
  return theme.transitions.create(
    ['transform', 'box-shadow'],
    {
      duration: theme.transitions.duration.shorter, // 200ms
      easing: theme.transitions.easing.easeOut,
    }
  );
};

/**
 * hoverLift transition과 함께 사용할 기본 lift 값
 * Button 컴포넌트에서 사용하는 값과 동일
 */
export const HOVER_LIFT_DEFAULT = '-1px';

/**
 * 복잡한 애니메이션을 위한 transition
 * 모달, 드로어 등 복잡한 상태 변화가 있는 요소에 사용
 * 
 * @param theme - MUI Theme 객체
 * @returns CSS transition 문자열
 */
export const complexAnimation = (theme: Theme): string => {
  return theme.transitions.create(
    ['background-color', 'transform', 'opacity', 'box-shadow', 'border-color'],
    {
      duration: theme.transitions.duration.complex, // 375ms
      easing: theme.transitions.easing.easeInOut,
    }
  );
};

